import type { DungeonEncounterKind } from "./dungeon";
import type { DungeonCharacterStats } from "./dungeon-character";
import {
  initializeDungeonChallenge,
  resolveDungeonChallengeExchange,
  type DungeonChallengeState,
} from "./dungeon-challenges";

export const DUNGEON_COMBAT_ENGINE_VERSION = 6 as const;

export type DungeonActionKind = "assault" | "defense" | "focus";
export type DungeonIntentKind = "attack" | "guard" | "channel";
export type DungeonActionOutcome = "counter" | "resisted" | "recovering";
export type DungeonCombatStatus = "player-ready" | "victory" | "defeat";
export type DungeonOpponentActor = "enemy" | "mechanism" | "seal";

export type DungeonOpponentOutcome =
  | "parried"
  | "absorbed"
  | "attack"
  | "riposte"
  | "spellstrike"
  | "guard-broken"
  | "fortify"
  | "interrupted"
  | "empower";

export interface DungeonActionSequenceResolution {
  outcome: DungeonActionOutcome;
  progress: 0 | 1 | 3 | 5;
  timeAdjustmentMs: number;
  scoreMultiplier: number;
  wardGain: 0 | 1 | 2;
  grantsOpening: boolean;
  consumesOpening: boolean;
}

export interface DungeonCombatState {
  version: 1 | 2 | 3 | 4 | 5 | typeof DUNGEON_COMBAT_ENGINE_VERSION;
  challenge?: DungeonChallengeState;
  encounterId: string;
  encounterKind: DungeonEncounterKind;
  danger: number;
  round: number;
  commandSequence: number;
  status: DungeonCombatStatus;
  intent: DungeonIntentKind;
  previousIntent: DungeonIntentKind | null;
  patternSeed: number;
  patternCursor: number;
  lastPlayerAction: DungeonActionKind | null;
  integrity: number;
  maximumIntegrity: number;
  opening: boolean;
  enemyPower: number;
  reactionWindowMs: number | null;
  timeRemainingMs: number;
  damageOnMistake: number;
  damageOnTimeout: number;
}

export interface DungeonCombatResources {
  /** Frozen at expedition launch. Training/sync changes apply next run. */
  character?: { level: number; stats: DungeonCharacterStats };
  vitality: number;
  maximumVitality: number;
  ward: number;
  weaponBonus: number;
  protectionBonus: number;
  armorBonus?: number;
  potions?: number;
  /** One-use vitality granted by Aegis rewards; healing never refills it. */
  temporaryVitality?: number;
}

export interface DungeonDamageResult {
  totalDamage: number;
  temporaryDamage: number;
  vitalityDamage: number;
}

/** Mutates only the supplied resource copy. Armor and ward resolve first. */
export function applyDungeonDamage(resources: DungeonCombatResources, amount: number): DungeonDamageResult {
  const totalDamage = Math.max(0, Math.round(amount));
  const temporaryDamage = Math.min(Math.max(0, resources.temporaryVitality || 0), totalDamage);
  const vitalityDamage = Math.min(Math.max(0, resources.vitality), totalDamage - temporaryDamage);
  resources.temporaryVitality = Math.max(0, (resources.temporaryVitality || 0) - temporaryDamage);
  resources.vitality = Math.max(0, resources.vitality - vitalityDamage);
  return { totalDamage, temporaryDamage, vitalityDamage };
}

export interface DungeonPlayerResult {
  action: DungeonActionKind;
  outcome: DungeonActionOutcome;
  integrityDamage: number;
  wardGain: number;
  openingGranted: boolean;
  openingConsumed: boolean;
  scoreMultiplier?: number;
  swift?: boolean;
  label?: string;
}

export interface DungeonOpponentResult {
  actor: DungeonOpponentActor;
  intent: DungeonIntentKind;
  outcome: DungeonOpponentOutcome;
  countered: boolean;
  fullStrength: boolean;
  vitalityDamage: number;
  temporaryDamage?: number;
  integrityRestore: number;
  powerChange: number;
  wardSpent: number;
}

export type DungeonCombatCommand =
  | {
      type: "submit-action";
      action: DungeonActionKind;
      firstAttemptCorrect: boolean;
      timing?: { elapsedMs: number; wordLength: number };
    }
  | { type: "mistype" }
  | { type: "threat-expired" }
  | { type: "use-potion" };

export type DungeonCombatPresentationBeat =
  | "player-action"
  | "player-result"
  | "opponent-action"
  | "opponent-result"
  | "next-intent"
  | "opportunity"
  | "victory"
  | "defeat";

export interface DungeonCombatPresentationEvent {
  beat: DungeonCombatPresentationBeat;
  actor: DungeonOpponentActor | "player";
  action?: DungeonActionKind | "potion";
  intent?: DungeonIntentKind;
  outcome?: DungeonActionOutcome | DungeonOpponentOutcome;
  amount?: number;
  temporaryAmount?: number;
  label: string;
}

export interface DungeonCombatExchange {
  id: string;
  accepted: boolean;
  command: DungeonCombatCommand["type"];
  nextCombat: DungeonCombatState;
  nextResources: DungeonCombatResources;
  playerResult: DungeonPlayerResult | null;
  opponentResult: DungeonOpponentResult | null;
  events: readonly DungeonCombatPresentationEvent[];
}

export interface CreateDungeonCombatInput {
  character?: DungeonCombatResources["character"];
  legacyRules?: boolean;
  difficulty?: "explorer" | "adventurer" | "nightmare";
  runSeed: number;
  encounterId: string;
  encounterKind: DungeonEncounterKind;
  danger: number;
  maximumIntegrity: number;
  reactionWindowMs: number | null;
  damageOnMistake: number;
  damageOnTimeout: number;
}

const DUNGEON_INTENT_COUNTERS: Readonly<
  Record<DungeonIntentKind, DungeonActionKind>
> = Object.freeze({
  attack: "defense",
  guard: "focus",
  channel: "assault",
});

const INTENT_PROFILES = {
  entrance: ["attack"],
  // Duplicate entries are deliberate weights. Monsters should most often
  // threaten a direct exchange; guard is a tactical change-up, not the
  // dominant beat players see every other turn.
  monster: ["attack", "channel", "attack", "guard", "attack"],
  trap: ["attack", "channel", "guard"],
  cache: ["guard", "channel", "attack"],
  boss: ["attack", "channel", "attack", "guard", "channel", "attack"],
} as const satisfies Readonly<
  Record<DungeonEncounterKind, readonly DungeonIntentKind[]>
>;

function hashText(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function clampInteger(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, Math.floor(value)));
}

function cloneCombat(state: DungeonCombatState): DungeonCombatState {
  return { ...state };
}

function cloneResources(resources: DungeonCombatResources): DungeonCombatResources {
  return { ...resources };
}

export function getDungeonCounterAction(
  intent: DungeonIntentKind,
): DungeonActionKind {
  return DUNGEON_INTENT_COUNTERS[intent] ?? "defense";
}

export function getDungeonOpponentActor(
  encounterKind: DungeonEncounterKind,
): DungeonOpponentActor {
  if (encounterKind === "trap") return "mechanism";
  if (encounterKind === "cache") return "seal";
  return "enemy";
}

export function createDungeonCombatPatternSeed(
  runSeed: number,
  encounterId: string,
): number {
  return hashText(`${Math.floor(runSeed)}:${encounterId}:combat-v1`);
}

export function selectDungeonIntent(input: {
  patternSeed: number;
  patternCursor: number;
  encounterKind: DungeonEncounterKind;
  previousIntent: DungeonIntentKind | null;
  lastPlayerAction: DungeonActionKind | null;
}): DungeonIntentKind {
  const profile: readonly DungeonIntentKind[] =
    INTENT_PROFILES[input.encounterKind] ?? INTENT_PROFILES.monster;
  const candidates = profile.filter((intent) => {
    if (profile.length > 1 && intent === input.previousIntent) return false;
    if (
      profile.length > 1 &&
      input.lastPlayerAction !== null &&
      getDungeonCounterAction(intent) === input.lastPlayerAction
    ) {
      return false;
    }
    return true;
  });
  const safeCandidates = candidates.length > 0
    ? candidates
    : profile.filter((intent) => intent !== input.previousIntent);
  const finalCandidates = safeCandidates.length > 0 ? safeCandidates : profile;
  const choice = hashText(
    `${input.patternSeed}:${input.patternCursor}:${input.encounterKind}`,
  ) % finalCandidates.length;
  return finalCandidates[choice] ?? "attack";
}

export function createDungeonCombatState(
  input: CreateDungeonCombatInput,
): DungeonCombatState {
  const patternSeed = createDungeonCombatPatternSeed(
    input.runSeed,
    input.encounterId,
  );
  const intent = selectDungeonIntent({
    patternSeed,
    patternCursor: 0,
    encounterKind: input.encounterKind,
    previousIntent: null,
    lastPlayerAction: null,
  });
  const maximumIntegrity = Math.max(1, Math.floor(input.maximumIntegrity));
  const combat: DungeonCombatState = {
    version: DUNGEON_COMBAT_ENGINE_VERSION,
    encounterId: input.encounterId,
    encounterKind: input.encounterKind,
    danger: Math.max(0, Math.floor(input.danger)),
    round: 1,
    commandSequence: 0,
    status: "player-ready",
    intent,
    previousIntent: null,
    patternSeed,
    patternCursor: 0,
    lastPlayerAction: null,
    integrity: maximumIntegrity,
    maximumIntegrity,
    opening: false,
    enemyPower: 0,
    reactionWindowMs: input.reactionWindowMs,
    timeRemainingMs: input.reactionWindowMs ?? 0,
    damageOnMistake: Math.max(1, Math.floor(input.damageOnMistake)),
    damageOnTimeout: Math.max(1, Math.floor(input.damageOnTimeout)),
  };
  initializeDungeonChallenge(combat, input);
  return combat;
}

export function resolveDungeonActionSequence(input: {
  action: DungeonActionKind;
  intent: DungeonIntentKind;
  previousAction: DungeonActionKind | null;
  encounterKind?: DungeonEncounterKind;
  hasOpening?: boolean;
  focusTimeBonusMs?: number;
}): DungeonActionSequenceResolution {
  const focusTimeBonusMs = Math.max(0, input.focusTimeBonusMs ?? 2_500);
  if (input.previousAction === input.action) {
    return {
      outcome: "recovering",
      progress: 0,
      timeAdjustmentMs: -1_200,
      scoreMultiplier: 0,
      wardGain: 0,
      grantsOpening: false,
      consumesOpening: false,
    };
  }
  if (getDungeonCounterAction(input.intent) === input.action) {
    if (input.encounterKind === "trap") {
      return {
        outcome: "counter",
        progress: 1,
        timeAdjustmentMs: input.action === "focus" ? focusTimeBonusMs : 500,
        scoreMultiplier: input.action === "assault" ? 1.35 : 1.15,
        wardGain: 0,
        grantsOpening: false,
        consumesOpening: false,
      };
    }
    const consumesOpening = input.action === "assault" && Boolean(input.hasOpening);
    const progress = input.action === "assault"
      ? consumesOpening ? 5 : 3
      : 1;
    return {
      outcome: "counter",
      progress,
      timeAdjustmentMs: input.action === "focus" ? focusTimeBonusMs : 500,
      scoreMultiplier: input.action === "assault" ? 1.5 : 1.15,
      wardGain: input.action === "defense" ? 2 : 0,
      grantsOpening: input.action === "focus",
      consumesOpening,
    };
  }
  const consumesOpening = input.action === "assault" && Boolean(input.hasOpening);
  return {
    outcome: "resisted",
    progress: 0,
    timeAdjustmentMs: input.action === "focus" ? focusTimeBonusMs : -900,
    scoreMultiplier: 0.5,
    wardGain: input.action === "defense" ? 1 : 0,
    grantsOpening: false,
    consumesOpening,
  };
}

function resolveOpponentResult(input: {
  combat: DungeonCombatState;
  resources: DungeonCombatResources;
  countered: boolean;
  fullStrength: boolean;
}): DungeonOpponentResult {
  const { combat } = input;
  const actor = getDungeonOpponentActor(combat.encounterKind);
  const isEnemy = actor === "enemy";
  const isMechanism = actor === "mechanism";
  if (combat.intent === "attack") {
    if (input.countered) {
      return {
        actor,
        intent: combat.intent,
        outcome: "parried",
        countered: true,
        fullStrength: input.fullStrength,
        vitalityDamage: 0,
        integrityRestore: 0,
        powerChange: 0,
        wardSpent: 0,
      };
    }
    if (input.resources.ward > 0) {
      return {
        actor,
        intent: combat.intent,
        outcome: "absorbed",
        countered: false,
        fullStrength: input.fullStrength,
        vitalityDamage: 0,
        integrityRestore: 0,
        powerChange: -combat.enemyPower,
        wardSpent: 1,
      };
    }
    const baseDamage = input.fullStrength
      ? combat.damageOnTimeout
      : Math.max(2, Math.ceil(combat.damageOnTimeout * 0.5));
    return {
      actor,
      intent: combat.intent,
      outcome: "attack",
      countered: false,
      fullStrength: input.fullStrength,
      vitalityDamage: baseDamage + combat.enemyPower * 3,
      integrityRestore: 0,
      powerChange: -combat.enemyPower,
      wardSpent: 0,
    };
  }
  if (combat.intent === "guard") {
    if (input.countered) {
      return {
        actor,
        intent: combat.intent,
        outcome: "guard-broken",
        countered: true,
        fullStrength: input.fullStrength,
        vitalityDamage: 0,
        integrityRestore: 0,
        powerChange: 0,
        wardSpent: 0,
      };
    }
    if (isMechanism) {
      const damage = input.fullStrength
        ? combat.damageOnTimeout
        : Math.max(2, Math.ceil(combat.damageOnTimeout * 0.5));
      return {
        actor,
        intent: combat.intent,
        outcome: "attack",
        countered: false,
        fullStrength: input.fullStrength,
        vitalityDamage: damage,
        integrityRestore: 0,
        powerChange: 0,
        wardSpent: 0,
      };
    }
    if (isEnemy && input.resources.ward > 0) {
      return {
        actor,
        intent: combat.intent,
        outcome: "absorbed",
        countered: false,
        fullStrength: input.fullStrength,
        vitalityDamage: 0,
        integrityRestore: 0,
        powerChange: 0,
        wardSpent: 1,
      };
    }
    if (isEnemy) {
      return {
        actor,
        intent: combat.intent,
        outcome: "riposte",
        countered: false,
        fullStrength: input.fullStrength,
        vitalityDamage: Math.max(2, Math.ceil(combat.damageOnTimeout * 0.34)),
        integrityRestore: 1,
        powerChange: 0,
        wardSpent: 0,
      };
    }
    return {
      actor,
      intent: combat.intent,
      outcome: "fortify",
      countered: false,
      fullStrength: input.fullStrength,
      vitalityDamage: 0,
      integrityRestore: 2,
      powerChange: 0,
      wardSpent: 0,
    };
  }
  if (input.countered) {
    return {
      actor,
      intent: combat.intent,
      outcome: "interrupted",
      countered: true,
      fullStrength: input.fullStrength,
      vitalityDamage: 0,
      integrityRestore: 0,
      powerChange: 0,
      wardSpent: 0,
    };
  }
  if (isMechanism) {
    const damage = input.fullStrength
      ? combat.damageOnTimeout
      : Math.max(2, Math.ceil(combat.damageOnTimeout * 0.6));
    return {
      actor,
      intent: combat.intent,
      outcome: "attack",
      countered: false,
      fullStrength: input.fullStrength,
      vitalityDamage: damage,
      integrityRestore: 0,
      powerChange: 0,
      wardSpent: 0,
    };
  }
  if (isEnemy && input.resources.ward > 0) {
    return {
      actor,
      intent: combat.intent,
      outcome: "absorbed",
      countered: false,
      fullStrength: input.fullStrength,
      vitalityDamage: 0,
      integrityRestore: 0,
      powerChange: 0,
      wardSpent: 1,
    };
  }
  if (isEnemy) {
    return {
      actor,
      intent: combat.intent,
      outcome: "spellstrike",
      countered: false,
      fullStrength: input.fullStrength,
      vitalityDamage:
        Math.max(3, Math.ceil(combat.damageOnTimeout * 0.45)) +
        combat.enemyPower * 2,
      integrityRestore: 0,
      powerChange: 1,
      wardSpent: 0,
    };
  }
  return {
    actor,
    intent: combat.intent,
    outcome: "empower",
    countered: false,
    fullStrength: input.fullStrength,
    vitalityDamage: 0,
    integrityRestore: 0,
    powerChange: 1,
    wardSpent: 0,
  };
}

function opponentActionLabel(
  actor: DungeonOpponentActor,
  intent: DungeonIntentKind,
): string {
  const labels = {
    enemy: {
      attack: "Enemy counterattacks",
      guard: "Enemy launches a guarded riposte",
      channel: "Enemy unleashes a spellstrike",
    },
    mechanism: {
      attack: "Trigger snaps shut",
      guard: "Pressure plates shift",
      channel: "Runes reach ignition",
    },
    seal: { attack: "Seal lashes out", guard: "Seal repairs", channel: "Seal gathers power" },
  } as const;
  return labels[actor][intent];
}

function opponentResultLabel(result: DungeonOpponentResult): string {
  if (result.temporaryDamage) {
    const vitalityLoss = Math.max(0, result.vitalityDamage - result.temporaryDamage);
    return `Aegis −${result.temporaryDamage}${vitalityLoss ? ` · vitality −${vitalityLoss}` : ""}`;
  }
  if (result.outcome === "attack") return `${result.vitalityDamage} vitality lost`;
  if (result.outcome === "riposte") {
    return `Riposte · ${result.vitalityDamage} vitality lost · guard +${result.integrityRestore}`;
  }
  if (result.outcome === "spellstrike") {
    return `Spellstrike · ${result.vitalityDamage} vitality lost · power rising`;
  }
  if (result.outcome === "absorbed") return "Ward absorbs the strike";
  if (result.outcome === "parried") return "Attack parried";
  if (result.outcome === "fortify") return `Integrity restored +${result.integrityRestore}`;
  if (result.outcome === "guard-broken") return "Guard broken";
  if (result.outcome === "empower") return `Power +${result.powerChange * 3}`;
  return "Channel interrupted";
}

function opportunityLabel(
  actor: DungeonOpponentActor,
  damage: number,
): string {
  if (actor === "mechanism") return `Trap misfire · ${damage} vitality lost`;
  if (actor === "seal") return `Vault hex backlash · ${damage} vitality lost`;
  return `Attack of opportunity · ${damage} vitality lost`;
}

function advanceCombatAfterOpponent(
  combat: DungeonCombatState,
  lastPlayerAction: DungeonActionKind | null,
): void {
  const currentIntent = combat.intent;
  const nextCursor = combat.patternCursor + 1;
  combat.previousIntent = currentIntent;
  combat.patternCursor = nextCursor;
  combat.intent = selectDungeonIntent({
    patternSeed: combat.patternSeed,
    patternCursor: nextCursor,
    encounterKind: combat.encounterKind,
    previousIntent: currentIntent,
    lastPlayerAction,
  });
  combat.round += 1;
}

function applyOpponentResult(
  combat: DungeonCombatState,
  resources: DungeonCombatResources,
  result: DungeonOpponentResult,
): void {
  resources.ward = Math.max(0, resources.ward - result.wardSpent);
  result.temporaryDamage = applyDungeonDamage(resources, result.vitalityDamage).temporaryDamage;
  combat.enemyPower = Math.max(0, combat.enemyPower + result.powerChange);
  combat.integrity = Math.min(
    combat.maximumIntegrity,
    combat.integrity + result.integrityRestore,
  );
  if (resources.vitality <= 0) combat.status = "defeat";
}

function createExchangeId(
  combat: DungeonCombatState,
  command: DungeonCombatCommand,
): string {
  const commandKey = command.type === "submit-action"
    ? `${command.type}:${command.action}:${command.firstAttemptCorrect}`
    : command.type;
  return `combat-${hashText(
    `${combat.encounterId}:${combat.round}:${combat.commandSequence}:${combat.patternCursor}:${commandKey}`,
  ).toString(36)}`;
}

export function resolveDungeonCombatExchange(input: {
  combat: DungeonCombatState;
  resources: DungeonCombatResources;
  command: DungeonCombatCommand;
}): DungeonCombatExchange {
  if (input.combat.challenge) {
    return resolveDungeonChallengeExchange({ ...input, id: createExchangeId(input.combat, input.command) });
  }
  const combat = cloneCombat(input.combat);
  const resources = cloneResources(input.resources);
  const id = createExchangeId(combat, input.command);
  if (combat.status !== "player-ready") {
    return {
      id,
      accepted: false,
      command: input.command.type,
      nextCombat: combat,
      nextResources: resources,
      playerResult: null,
      opponentResult: null,
      events: [],
    };
  }

  // Saved legacy encounters keep their original combat rules. The current
  // challenge engine handles tactical potion use; old encounters reject it.
  if (input.command.type === "use-potion") {
    return { id, accepted: false, command: input.command.type, nextCombat: combat,
      nextResources: resources, playerResult: null, opponentResult: null, events: [] };
  }

  if (input.command.type === "mistype") {
    combat.commandSequence += 1;
    const damage = combat.damageOnMistake;
    const actor = getDungeonOpponentActor(combat.encounterKind);
    const applied = applyDungeonDamage(resources, damage);
    if (resources.vitality <= 0) combat.status = "defeat";
    if (combat.reactionWindowMs !== null) {
      combat.timeRemainingMs = Math.max(
        combat.timeRemainingMs,
        Math.min(1_500, combat.reactionWindowMs * 0.2),
      );
    }
    const events: DungeonCombatPresentationEvent[] = [
      {
        beat: "opportunity",
        actor,
        intent: combat.intent,
        amount: damage,
        temporaryAmount: applied.temporaryDamage,
        label: applied.temporaryDamage > 0
          ? `Aegis absorbs ${applied.temporaryDamage}${applied.vitalityDamage ? ` · ${applied.vitalityDamage} vitality lost` : ""}`
          : opportunityLabel(actor, damage),
      },
    ];
    if (resources.vitality <= 0) {
      events.push({ beat: "defeat", actor: "player", label: "Expedition defeated" });
    }
    return {
      id,
      accepted: true,
      command: input.command.type,
      nextCombat: combat,
      nextResources: resources,
      playerResult: null,
      opponentResult: null,
      events,
    };
  }

  if (input.command.type === "threat-expired") {
    combat.commandSequence += 1;
    const opponentResult = resolveOpponentResult({
      combat,
      resources,
      countered: false,
      fullStrength: true,
    });
    applyOpponentResult(combat, resources, opponentResult);
    const events: DungeonCombatPresentationEvent[] = [
      {
        beat: "opponent-action",
        actor: opponentResult.actor,
        intent: opponentResult.intent,
        label: opponentActionLabel(opponentResult.actor, opponentResult.intent),
      },
      {
        beat: "opponent-result",
        actor: opponentResult.actor,
        intent: opponentResult.intent,
        outcome: opponentResult.outcome,
        amount: opponentResult.vitalityDamage || opponentResult.integrityRestore || undefined,
        temporaryAmount: opponentResult.temporaryDamage,
        label: opponentResultLabel(opponentResult),
      },
    ];
    if (resources.vitality <= 0) {
      events.push({ beat: "defeat", actor: "player", label: "Expedition defeated" });
    } else {
      advanceCombatAfterOpponent(combat, combat.lastPlayerAction);
      combat.timeRemainingMs = combat.reactionWindowMs ?? 0;
      events.push({
        beat: "next-intent",
        actor: getDungeonOpponentActor(combat.encounterKind),
        intent: combat.intent,
        label: `Next intent · ${combat.intent}`,
      });
    }
    return {
      id,
      accepted: true,
      command: input.command.type,
      nextCombat: combat,
      nextResources: resources,
      playerResult: null,
      opponentResult,
      events,
    };
  }

  const actionResolution = resolveDungeonActionSequence({
    action: input.command.action,
    intent: combat.intent,
    previousAction: combat.lastPlayerAction,
    encounterKind: combat.encounterKind,
    hasOpening: combat.opening,
    focusTimeBonusMs: 2_000 + combat.danger * 500,
  });
  if (actionResolution.outcome === "recovering") {
    return {
      id,
      accepted: false,
      command: input.command.type,
      nextCombat: combat,
      nextResources: resources,
      playerResult: {
        action: input.command.action,
        outcome: "recovering",
        integrityDamage: 0,
        wardGain: 0,
        openingGranted: false,
        openingConsumed: false,
      },
      opponentResult: null,
      events: [],
    };
  }

  combat.commandSequence += 1;

  const weaponDamage =
    actionResolution.outcome === "counter" &&
    input.command.action === "assault" &&
    combat.encounterKind !== "trap"
      ? resources.weaponBonus
      : 0;
  const integrityDamage = actionResolution.progress + weaponDamage;
  combat.integrity = Math.max(0, combat.integrity - integrityDamage);
  resources.ward = clampInteger(
    resources.ward + actionResolution.wardGain,
    0,
    2,
  );
  if (actionResolution.consumesOpening) combat.opening = false;
  if (actionResolution.grantsOpening) combat.opening = true;
  combat.lastPlayerAction = input.command.action;
  const playerResult: DungeonPlayerResult = {
    action: input.command.action,
    outcome: actionResolution.outcome,
    integrityDamage,
    wardGain: actionResolution.wardGain,
    openingGranted: actionResolution.grantsOpening,
    openingConsumed: actionResolution.consumesOpening,
  };
  const events: DungeonCombatPresentationEvent[] = [
    {
      beat: "player-action",
      actor: "player",
      action: input.command.action,
      label: `Player uses ${input.command.action}`,
    },
    {
      beat: "player-result",
      actor: "player",
      action: input.command.action,
      outcome: actionResolution.outcome,
      amount: integrityDamage || undefined,
      label: actionResolution.outcome === "counter"
        ? `Countered · ${integrityDamage} integrity`
        : "Resisted · no integrity damage",
    },
  ];

  if (combat.integrity <= 0) {
    combat.status = "victory";
    events.push({ beat: "victory", actor: "player", label: "Threat defeated" });
    return {
      id,
      accepted: true,
      command: input.command.type,
      nextCombat: combat,
      nextResources: resources,
      playerResult,
      opponentResult: null,
      events,
    };
  }

  const opponentResult = resolveOpponentResult({
    combat,
    resources,
    countered: actionResolution.outcome === "counter",
    fullStrength: false,
  });
  applyOpponentResult(combat, resources, opponentResult);
  events.push(
    {
      beat: "opponent-action",
      actor: opponentResult.actor,
      intent: opponentResult.intent,
      label: opponentActionLabel(opponentResult.actor, opponentResult.intent),
    },
    {
      beat: "opponent-result",
      actor: opponentResult.actor,
      intent: opponentResult.intent,
      outcome: opponentResult.outcome,
      amount:
        opponentResult.vitalityDamage ||
        opponentResult.integrityRestore ||
        Math.max(0, opponentResult.powerChange * 3) ||
        undefined,
      temporaryAmount: opponentResult.temporaryDamage,
      label: opponentResultLabel(opponentResult),
    },
  );
  if (resources.vitality <= 0) {
    events.push({ beat: "defeat", actor: "player", label: "Expedition defeated" });
  } else {
    advanceCombatAfterOpponent(combat, input.command.action);
    if (combat.reactionWindowMs !== null) {
      combat.timeRemainingMs = Math.min(
        combat.reactionWindowMs * 1.5,
        Math.max(
          0,
          combat.reactionWindowMs + actionResolution.timeAdjustmentMs,
        ),
      );
    }
    events.push({
      beat: "next-intent",
      actor: opponentResult.actor,
      intent: combat.intent,
      label: `Next intent · ${combat.intent}`,
    });
  }
  return {
    id,
    accepted: true,
    command: input.command.type,
    nextCombat: combat,
    nextResources: resources,
    playerResult,
    opponentResult,
    events,
  };
}

// Compatibility helper for non-runtime consumers while the full exchange is
// the authoritative path used by the game.
export function resolveDungeonEnemyTurn(input: {
  intent: DungeonIntentKind;
  countered: boolean;
  ward: number;
  enemyPower: number;
  baseDamage: number;
}): {
  outcome: DungeonOpponentOutcome;
  damage: number;
  integrityRestore: number;
  powerChange: number;
  wardSpent: number;
} {
  const combat: DungeonCombatState = {
    version: DUNGEON_COMBAT_ENGINE_VERSION,
    encounterId: "compatibility",
    encounterKind: "monster",
    danger: 1,
    round: 1,
    commandSequence: 0,
    status: "player-ready",
    intent: input.intent,
    previousIntent: null,
    patternSeed: 1,
    patternCursor: 0,
    lastPlayerAction: null,
    integrity: 10,
    maximumIntegrity: 10,
    opening: false,
    enemyPower: input.enemyPower,
    reactionWindowMs: 8_500,
    timeRemainingMs: 8_500,
    damageOnMistake: 4,
    damageOnTimeout: Math.max(1, input.baseDamage * 2),
  };
  const result = resolveOpponentResult({
    combat,
    resources: {
      vitality: 100,
      maximumVitality: 100,
      ward: input.ward,
      weaponBonus: 0,
      protectionBonus: 0,
    },
    countered: input.countered,
    fullStrength: false,
  });
  return {
    outcome: result.outcome,
    damage: result.vitalityDamage,
    integrityRestore: result.integrityRestore,
    powerChange: result.powerChange,
    wardSpent: result.wardSpent,
  };
}
